import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import Loading from 'components/World/Loading';
import World from 'components/World';
import {
  resolveToken,
  isMockMode,
  fetchNextLesson,
  postLessonHeartbeat,
  putKeyTermProgress,
  putLessonProgress,
  getCurrentLessonStatus,
  getLessonProgress,
  onV2AuthExpired,
} from 'services/LessonContentService';
import {
  playTutorAudio,
  pauseTutorSpeech,
  queueTutorAudioPrefetch,
  resumeTutorSpeech,
  getTutorSpeechPlaybackRate,
  setTutorSpeechPlaybackRate,
  getTutorSpeechVolume,
  setTutorSpeechVolume,
  stopTutorSpeech,
  stopTutorChatSpeech,
} from 'services/TextToSpeechService';
import { WEB_APP_BASE_URL } from 'config/app.config';
import {
  fetchLessonContentAction,
  advancePhase,
  setTutorialViewed,
  setWorkbookReady,
  setWorkbookDone,
  submitWorkbookAnswer,
  submitKCAnswer,
  advanceKCQuestion,
  kcSummaryDone,
  submitMasteryAnswer,
  advanceMasteryQuestion,
  jumpToDevTimelineNode,
  navigateToTrackerNode,
  resetViewer,
  retakeMasteryTest,
  resetNarration,
  setNarrationPending,
  setNarrationPlaying,
  setNarrationComplete,
  setNarrationError,
  setTutorChatOpen,
  startTermReview,
  startSpeaking,
  stopSpeaking,
  resumeViewerFromBackend,
  fetchKeyTermProgressAction,
  setKeyTermProgress,
} from 'store/lessonContentViewer/actions';
import {
  selectViewerLoading,
  selectViewerError,
  selectViewerModel,
  selectCurrentPhase,
  selectActiveTerm,
  selectActiveTermIndex,
  selectActiveLearningStep,
  selectActiveLearningStepIndex,
  selectActiveQuestionIndex,
  selectActiveWorkbook,
  selectWorkbookState,
  selectActiveKCQuestion,
  selectKCAnswers,
  selectKCScore,
  selectActiveMasteryQuestion,
  selectMasteryAnswers,
  selectMasteryScore,
  selectProgressTrackerData,
  selectTutorText,
  selectNarrationTargets,
  selectShowDevTools,
  selectTutorChatOpen,
  selectFrontloadingData,
  selectMetadata,
  selectMasteryQuestions,
  selectLessonViewerUnityWorldLoading,
  selectIsNarrationGateOpen,
  selectKeyTermProgressMap,
} from 'store/lessonContentViewer/selectors';
import LCVFrontloadingStage from './components/LCVFrontloadingStage';
import LCVStaticLessonStage from './components/LCVStaticLessonStage';
import LCVActionSidebar from './components/LCVActionSidebar';
import LCVDevStrip from './components/LCVDevStrip';
import LCVTutorChatPanel from './components/LCVTutorChatPanel';
import { buildTutorChatContext } from './components/buildTutorChatContext';
import TutorialPhase from './components/TutorialPhase';
import LearningPhase from './components/LearningPhase';
import WorkbookReadyPhase from './components/WorkbookReadyPhase';
import WorkbookOptionsPhase from './components/WorkbookOptionsPhase';
import KnowledgeCheckPhase from './components/KnowledgeCheckPhase';
import KCSummaryPhase from './components/KCSummaryPhase';
import MasteryPhase from './components/MasteryPhase';
import ResultsPhase from './components/ResultsPhase';
import LessonViewerUnityBackground from './LessonViewerUnityBackground';
import './LessonContentViewer.css';

/** Opaque black behind the progress bar so Unity / lesson UI do not show through. */
const LCV_UNITY_LOADING_BG = '#000000';
/** ~1%/s default is too slow for long Unity init; keep bar moving toward cap while overlay is up. */
const LCV_UNITY_LOADING_FAKE_RATE_PER_SEC = 9;
const MIN_TUTOR_SPEECH_DURATION_MS = 1800;
const ESTIMATED_MS_PER_WORD = 320;
const TUTOR_PLAYBACK_RATE_STEP = 0.1;
const TUTOR_PLAYBACK_RATE_MIN = 0.5;
const TUTOR_PLAYBACK_RATE_MAX = 1.5;
const TUTOR_VOLUME_STEP = 10;
const TUTOR_VOLUME_MIN = 0;
const TUTOR_VOLUME_MAX = 100;

function estimateTutorSpeechDurationMs(text) {
  const wordCount = String(text || '').trim().split(/\s+/).filter(Boolean).length;
  return Math.max(MIN_TUTOR_SPEECH_DURATION_MS, wordCount * ESTIMATED_MS_PER_WORD);
}

function clampProgress(value) {
  return Math.max(0, Math.min(1, value));
}

function normalizeTutorPlaybackRate(rate) {
  const roundedRate = Math.round(rate * 10) / 10;
  return Math.min(TUTOR_PLAYBACK_RATE_MAX, Math.max(TUTOR_PLAYBACK_RATE_MIN, roundedRate));
}

/**
 * Maps the viewer phase to the backend term phase value used by
 * lesson-activity heartbeats and key-term-progress. Returns null when the
 * phase has no active term (tutorial, frontloading, mastery, results).
 */
function mapPhaseToTermStatus(phase) {
  switch (phase) {
    case 'learning':
      return 'learning';
    case 'workbook_ready':
    case 'workbook_exercise':
    case 'workbook_options':
      return 'workbook';
    case 'knowledge_check':
      return 'knowledge_check';
    case 'knowledge_check_summary':
      return 'completed';
    default:
      return null;
  }
}

const DEFAULT_FALLBACK_LESSON_ID = 'G3-MATH-L013-720L';
const HEARTBEAT_INTERVAL_MS = 30_000;
/** Web room explore: auto-return to lesson POV and UI after this long in free roam. */
const ROOM_EXPLORE_SESSION_MS = 5 * 60 * 1000;

const LessonContentViewer = () => {
  const dispatch = useDispatch();

  const loading = useSelector(selectViewerLoading);
  const error = useSelector(selectViewerError);
  const viewerModel = useSelector(selectViewerModel);
  const phase = useSelector(selectCurrentPhase);
  const activeTerm = useSelector(selectActiveTerm);
  const activeTermIndex = useSelector(selectActiveTermIndex);
  const activeLearningStep = useSelector(selectActiveLearningStep);
  const activeLearningStepIndex = useSelector(selectActiveLearningStepIndex);
  const activeQuestionIndex = useSelector(selectActiveQuestionIndex);
  const activeWorkbook = useSelector(selectActiveWorkbook);
  const workbookState = useSelector(selectWorkbookState);
  const activeKCQuestion = useSelector(selectActiveKCQuestion);
  const kcAnswers = useSelector(selectKCAnswers);
  const kcScore = useSelector(selectKCScore);
  const activeMasteryQuestion = useSelector(selectActiveMasteryQuestion);
  const masteryAnswers = useSelector(selectMasteryAnswers);
  const masteryScore = useSelector(selectMasteryScore);
  const masteryQuestions = useSelector(selectMasteryQuestions);
  const progressData = useSelector(selectProgressTrackerData);
  const tutorText = useSelector(selectTutorText);
  const narrationTargets = useSelector(selectNarrationTargets);
  const showDevTools = useSelector(selectShowDevTools);
  const tutorChatOpen = useSelector(selectTutorChatOpen);
  const frontloadingData = useSelector(selectFrontloadingData);
  const metadata = useSelector(selectMetadata);
  const unityWorldLoading = useSelector(selectLessonViewerUnityWorldLoading);
  const isNarrationGateOpen = useSelector(selectIsNarrationGateOpen);
  const keyTermProgress = useSelector(selectKeyTermProgressMap);
  /**
   * Stays true after Redux clears `unityWorldLoading` until `Loading` finishes its exit animation.
   * Updated synchronously during render (no useEffect gap) so `Loading` never unmounts/remounts
   * and fake progress does not reset to a new random %.
   */
  const unityOverlayLockedRef = useRef(false);
  /** TEST: lip-sync once per lesson after Unity loading overlay dismisses. */
  const lcvTestSpeechAfterLoadRef = useRef(false);
  const [, bumpUnityOverlay] = useState(0);
  const [visualContent, setVisualContent] = useState(null);
  const [trackerMode, setTrackerMode] = useState('mini');
  const [tutorFontSize, setTutorFontSize] = useState(17);
  const [tutorPlaybackRate, setTutorPlaybackRate] = useState(() =>
    getTutorSpeechPlaybackRate(),
  );
  const [tutorVolume, setTutorVolume] = useState(() => getTutorSpeechVolume());
  const lastUnmutedTutorVolumeRef = useRef(
    Math.max(TUTOR_VOLUME_STEP, getTutorSpeechVolume()),
  );
  const [narrationPaused, setNarrationPaused] = useState(false);
  const [tutorReplayKey, setTutorReplayKey] = useState(0);
  const [nextLessonLoading, setNextLessonLoading] = useState(false);
  const [nextLessonUnavailable, setNextLessonUnavailable] = useState(false);
  const [nextLessonError, setNextLessonError] = useState('');
  const [roomExploreActive, setRoomExploreActive] = useState(false);
  const [roomExploreConsumed, setRoomExploreConsumed] = useState(false);
  const inRoomExploreRef = useRef(false);
  const [narrationPlayback, setNarrationPlayback] = useState({
    audioId: null,
    isPlaying: false,
    currentTimeMs: 0,
    durationMs: 0,
    complete: false,
    // Per-word timing data from the lesson-audio manifest (or a fresh
    // single-clip download). Drives precise subtitle highlighting in
    // LCVTutorDialog when present; falls back to the legacy
    // duration-based heuristic when the clip is from live ElevenLabs.
    wordTimings: null,
  });
  /**
   * Flipped to true when any v2 backend call returns 401. Renders a "sign
   * back in" popup and suspends further writes so we don't keep hammering
   * the backend after the session is gone.
   */
  const [authExpired, setAuthExpired] = useState(false);
  const authExpiredRef = useRef(false);

  const authToken = useMemo(() => resolveToken(), []);
  /** Mock mode loads bundled JSON and does not call the API; allow without JWT. */
  const lessonFetchToken = authToken || (isMockMode() ? 'mock' : null);

  const { urlLessonId, hadUrlLessonId, skipWorld } = useMemo(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const paramLessonId = params.get('lessonId');
      return {
        urlLessonId: paramLessonId || null,
        hadUrlLessonId: Boolean(paramLessonId),
        skipWorld: params.get('skipWorld') === 'true',
      };
    }
    return { urlLessonId: null, hadUrlLessonId: false, skipWorld: false };
  }, []);
  /**
   * Resolved lessonId used by the viewer. When the URL has one we use it
   * directly; otherwise we ask the backend via /current-lesson-status (or
   * fall back to a default when mock mode / no backend result).
   */
  const [lessonId, setLessonId] = useState(
    urlLessonId || DEFAULT_FALLBACK_LESSON_ID,
  );
  const [lessonIdResolved, setLessonIdResolved] = useState(
    () => hadUrlLessonId || isMockMode(),
  );
  /**
   * Web + Unity: tutor TTS must not start until the world loading bar has finished
   * and exited (see `onUnityLoadingExitAnimationComplete`). Native / skipWorld: open immediately.
   */
  const [lcvUnityTutorAudioGateOpen, setLcvUnityTutorAudioGateOpen] = useState(
    () => skipWorld || Platform.OS !== 'web',
  );
  const lessonAudioScope = useMemo(
    () => (lessonId ? `lesson:${lessonId}` : 'lesson:unknown'),
    [lessonId],
  );
  const currentNarrationTarget = narrationTargets[0] || null;
  const upcomingNarrationTargets = narrationTargets.slice(1, 3);
  const upcomingNarrationSignature = upcomingNarrationTargets
    .map(target => `${target.audioId}:${target.text}`)
    .join('||');
  const tutorAnimationProgress = useMemo(() => {
    if (
      !currentNarrationTarget?.audioId ||
      narrationPlayback.audioId !== currentNarrationTarget.audioId
    ) {
      return 0;
    }

    if (narrationPlayback.complete) {
      return 1;
    }

    const durationMs =
      narrationPlayback.durationMs ||
      estimateTutorSpeechDurationMs(currentNarrationTarget.text);

    if (!durationMs) {
      return 0;
    }

    return clampProgress(narrationPlayback.currentTimeMs / durationMs);
  }, [currentNarrationTarget, narrationPlayback]);
  const handleExit = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.location.assign(WEB_APP_BASE_URL);
    }
  }, []);
  const canPauseTutor = Boolean(currentNarrationTarget?.audioId) && !narrationPlayback.complete;
  const canSkipTutor = Boolean(currentNarrationTarget?.audioId) && !narrationPlayback.complete;
  const canIncreaseTutorPlaybackRate = tutorPlaybackRate < TUTOR_PLAYBACK_RATE_MAX;
  const canDecreaseTutorPlaybackRate = tutorPlaybackRate > TUTOR_PLAYBACK_RATE_MIN;
  const canIncreaseTutorVolume = tutorVolume < TUTOR_VOLUME_MAX;
  const canDecreaseTutorVolume = tutorVolume > TUTOR_VOLUME_MIN;
  const isTutorMuted = tutorVolume <= 0;
  const handleIncreaseTutorPlaybackRate = useCallback(() => {
    setTutorPlaybackRate(current => {
      const nextRate = normalizeTutorPlaybackRate(current + TUTOR_PLAYBACK_RATE_STEP);
      return setTutorSpeechPlaybackRate(nextRate);
    });
  }, []);
  const handleDecreaseTutorPlaybackRate = useCallback(() => {
    setTutorPlaybackRate(current => {
      const nextRate = normalizeTutorPlaybackRate(current - TUTOR_PLAYBACK_RATE_STEP);
      return setTutorSpeechPlaybackRate(nextRate);
    });
  }, []);
  const handleIncreaseTutorVolume = useCallback(() => {
    setTutorVolume(current => {
      const nextVolume = Math.min(TUTOR_VOLUME_MAX, current + TUTOR_VOLUME_STEP);
      if (nextVolume > 0) {
        lastUnmutedTutorVolumeRef.current = nextVolume;
      }
      return setTutorSpeechVolume(nextVolume);
    });
  }, []);
  const handleDecreaseTutorVolume = useCallback(() => {
    setTutorVolume(current => {
      const nextVolume = Math.max(TUTOR_VOLUME_MIN, current - TUTOR_VOLUME_STEP);
      if (nextVolume > 0) {
        lastUnmutedTutorVolumeRef.current = nextVolume;
      }
      return setTutorSpeechVolume(nextVolume);
    });
  }, []);
  const handleToggleTutorMute = useCallback(() => {
    setTutorVolume(current => {
      if (current <= 0) {
        const restoredVolume = Math.min(
          TUTOR_VOLUME_MAX,
          Math.max(TUTOR_VOLUME_STEP, lastUnmutedTutorVolumeRef.current || TUTOR_VOLUME_MAX),
        );
        lastUnmutedTutorVolumeRef.current = restoredVolume;
        return setTutorSpeechVolume(restoredVolume);
      }

      lastUnmutedTutorVolumeRef.current = current;
      return setTutorSpeechVolume(0);
    });
  }, []);
  const handlePauseTutor = useCallback(() => {
    if (!canPauseTutor) return;
    if (!pauseTutorSpeech()) return;

    stopSpeaking();
    setNarrationPaused(true);
    setNarrationPlayback(current => ({
      ...current,
      isPlaying: false,
    }));
  }, [canPauseTutor]);
  const handleResumeTutor = useCallback(() => {
    resumeTutorSpeech()
      .then(resumed => {
        if (!resumed) return;
        startSpeaking();
        setNarrationPaused(false);
        setNarrationPlayback(current => ({
          ...current,
          isPlaying: true,
        }));
      })
      .catch(() => {
        // noop - existing narration error handling covers playback failures
      });
  }, []);
  const handleSkipTutor = useCallback(() => {
    if (!canSkipTutor || !currentNarrationTarget?.audioId) return;

    stopTutorSpeech();
    stopSpeaking();
    setNarrationPaused(false);
    setNarrationPlayback(current => {
      if (current.audioId !== currentNarrationTarget.audioId) return current;

      const resolvedDurationMs =
        current.durationMs ||
        estimateTutorSpeechDurationMs(currentNarrationTarget.text);

      return {
        ...current,
        isPlaying: false,
        currentTimeMs: resolvedDurationMs,
        durationMs: resolvedDurationMs,
        complete: true,
      };
    });
    dispatch(setNarrationComplete(currentNarrationTarget.audioId));
  }, [
    canSkipTutor,
    currentNarrationTarget?.audioId,
    currentNarrationTarget?.text,
    dispatch,
  ]);
  const handleReplayTutor = useCallback(() => {
    if (!currentNarrationTarget?.audioId) return;

    stopTutorSpeech();
    stopSpeaking();
    setNarrationPaused(false);
    setTutorReplayKey(current => current + 1);
  }, [currentNarrationTarget?.audioId]);

  const buildNextLessonUrl = useCallback(nextLessonId => {
    if (typeof window === 'undefined') return null;

    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('screen', 'lessonViewer');
    nextUrl.searchParams.set('lessonId', nextLessonId);
    return nextUrl.toString();
  }, []);

  const handleGoToNextLesson = useCallback(async () => {
    if (!lessonFetchToken || nextLessonLoading) return;

    setNextLessonLoading(true);
    setNextLessonUnavailable(false);
    setNextLessonError('');

    try {
      const response = await fetchNextLesson(lessonId, lessonFetchToken);
      const nextLessonId = String(response?.lessonId || '').trim();

      if (!nextLessonId || nextLessonId.toLowerCase() === 'no more lessons') {
        setNextLessonUnavailable(true);
        return;
      }

      const nextLessonUrl = buildNextLessonUrl(nextLessonId);
      if (!nextLessonUrl) {
        throw new Error('Unable to build the next lesson URL.');
      }

      window.location.assign(nextLessonUrl);
    } catch (nextLessonFetchError) {
      setNextLessonError(
        nextLessonFetchError?.message || 'Unable to open the next lesson right now.',
      );
    } finally {
      setNextLessonLoading(false);
    }
  }, [buildNextLessonUrl, lessonFetchToken, lessonId, nextLessonLoading]);

  const handleReviewTerm = useCallback(
    termIndex => {
      setNextLessonUnavailable(false);
      setNextLessonError('');
      dispatch(startTermReview(termIndex));
    },
    [dispatch],
  );

  const handleRetakeMastery = useCallback(() => {
    setNextLessonUnavailable(false);
    setNextLessonError('');
    dispatch(retakeMasteryTest());
  }, [dispatch]);

  const showRoomExploreOffer =
    Platform.OS === 'web' &&
    !skipWorld &&
    !unityWorldLoading &&
    phase === 'results' &&
    masteryScore.passed &&
    !roomExploreActive &&
    !roomExploreConsumed;

  const startRoomExplore = useCallback(() => {
    if (Platform.OS !== 'web' || skipWorld || unityWorldLoading) return;
    if (phase !== 'results' || !masteryScore.passed) return;
    if (inRoomExploreRef.current) return;

    inRoomExploreRef.current = true;
    World.runCommand('use_player_camera');
    World.runCommand('lesson_controls_lock false');
    World.runCommand('enable_movement true');
    World.runCommand('player_camera THIRD');

    dispatch(setTutorChatOpen(false));
    setRoomExploreActive(true);
  }, [dispatch, masteryScore.passed, phase, skipWorld, unityWorldLoading]);

  const endRoomExploreSession = useCallback(() => {
    if (!inRoomExploreRef.current) return;
    inRoomExploreRef.current = false;

    if (Platform.OS === 'web' && !skipWorld) {
      World.runCommand('lesson_controls_lock true');
      World.runCommand('player_camera FIRST');
    }
    setRoomExploreActive(false);
    setRoomExploreConsumed(true);
  }, [skipWorld]);

  useEffect(() => {
    if (!roomExploreActive) return undefined;
    const id = setTimeout(() => {
      endRoomExploreSession();
    }, ROOM_EXPLORE_SESSION_MS);
    return () => clearTimeout(id);
  }, [roomExploreActive, endRoomExploreSession]);

  /**
   * Resolve lessonId: use URL param when present, otherwise (and not in mock
   * mode) ask the backend which lesson to resume. Falls back to the default
   * if the backend returns nothing.
   */
  useEffect(() => {
    if (lessonIdResolved) return undefined;
    if (!lessonFetchToken) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const status = await getCurrentLessonStatus(lessonFetchToken);
        if (cancelled) return;
        const resolved = status?.lessonId;
        if (resolved) {
          setLessonId(String(resolved));
        }
      } finally {
        if (!cancelled) setLessonIdResolved(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lessonIdResolved, lessonFetchToken]);

  useEffect(() => {
    if (!lessonFetchToken || !lessonIdResolved) return undefined;
    dispatch(fetchLessonContentAction(lessonId, lessonFetchToken, { skipWorld }));
    return () => dispatch(resetViewer());
  }, [dispatch, lessonId, lessonFetchToken, skipWorld, lessonIdResolved]);

  /**
   * Pre-populate the progress tracker with persisted per-key-term progress
   * (so terms the learner already cleared show as checked and are clickable
   * even when lesson-activity resumes them on an earlier term).
   */
  useEffect(() => {
    if (!lessonFetchToken || !lessonIdResolved) return undefined;
    if (isMockMode() || authExpired) return undefined;
    dispatch(fetchKeyTermProgressAction(lessonId, lessonFetchToken));
    return undefined;
  }, [dispatch, lessonId, lessonFetchToken, lessonIdResolved, authExpired]);

  useEffect(() => {
    setVisualContent(null);
  }, [lessonId]);

  useEffect(() => {
    setNextLessonLoading(false);
    setNextLessonUnavailable(false);
    setNextLessonError('');
    inRoomExploreRef.current = false;
    setRoomExploreActive(false);
    setRoomExploreConsumed(false);
  }, [lessonId]);

  useEffect(() => {
    unityOverlayLockedRef.current = false;
    lcvTestSpeechAfterLoadRef.current = false;
    setLcvUnityTutorAudioGateOpen(skipWorld || Platform.OS !== 'web');
    bumpUnityOverlay(n => n + 1);
  }, [lessonId, skipWorld]);

  useEffect(() => {
    setNarrationPaused(false);
  }, [currentNarrationTarget?.audioId]);

  /**
   * Auto pause lesson narration whenever the tutor chat opens, and resume
   * from the same position once it closes. AudioManager keeps only one
   * tutor voice playing at a time; the chat reply uses a separate channel
   * so the lesson narration can stay paused without being clobbered.
   */
  const tutorChatWasOpenRef = useRef(false);
  useEffect(() => {
    if (tutorChatOpen && !tutorChatWasOpenRef.current) {
      pauseTutorSpeech();
      stopSpeaking();
      setNarrationPaused(true);
      setNarrationPlayback(current => ({ ...current, isPlaying: false }));
    } else if (!tutorChatOpen && tutorChatWasOpenRef.current) {
      stopTutorChatSpeech();
      resumeTutorSpeech()
        .then(resumed => {
          if (!resumed) return;
          startSpeaking();
          setNarrationPaused(false);
          setNarrationPlayback(current => ({ ...current, isPlaying: true }));
        })
        .catch(() => {
          // Existing narration error handling covers playback failures.
        });
    }
    tutorChatWasOpenRef.current = tutorChatOpen;
  }, [tutorChatOpen]);

  const { tutorChatGreeting, tutorChatContext } = useMemo(() => {
    const built = buildTutorChatContext({
      phase,
      metadata,
      activeTerm,
      activeLearningStep,
      activeWorkbook,
      activeKCQuestion,
    });
    return {
      tutorChatGreeting: built.greeting,
      tutorChatContext: built.currentContext,
    };
  }, [
    phase,
    metadata,
    activeTerm,
    activeLearningStep,
    activeWorkbook,
    activeKCQuestion,
  ]);

  const handleCloseTutorChat = useCallback(() => {
    dispatch(setTutorChatOpen(false));
  }, [dispatch]);

  const onUnityLoadingExitAnimationComplete = useCallback(() => {
    unityOverlayLockedRef.current = false;
    setLcvUnityTutorAudioGateOpen(true);
    bumpUnityOverlay(n => n + 1);
    if (
      Platform.OS === 'web' &&
      !skipWorld &&
      !lcvTestSpeechAfterLoadRef.current
    ) {
      lcvTestSpeechAfterLoadRef.current = true;


      //TEST uncomment this line to make the tutor "speak" for 30 seconds after the loading screen.
      //startSpeaking(30_000);

    }
  }, [skipWorld]);

  useEffect(() => {
    if (!currentNarrationTarget?.audioId || !currentNarrationTarget?.text) {
      stopTutorSpeech();
      stopSpeaking();
      setNarrationPlayback({
        audioId: null,
        isPlaying: false,
        currentTimeMs: 0,
        durationMs: 0,
        complete: false,
        wordTimings: null,
      });
      dispatch(resetNarration());
      return undefined;
    }

    if (!lcvUnityTutorAudioGateOpen) {
      return undefined;
    }

    let cancelled = false;
    const fallbackDurationMs = estimateTutorSpeechDurationMs(currentNarrationTarget.text);

    setNarrationPlayback({
      audioId: currentNarrationTarget.audioId,
      isPlaying: false,
      currentTimeMs: 0,
      durationMs: fallbackDurationMs,
      complete: false,
      wordTimings: null,
    });
    dispatch(setNarrationPending(currentNarrationTarget.audioId));

    playTutorAudio({
      lessonScope: lessonAudioScope,
      audioId: currentNarrationTarget.audioId,
      text: currentNarrationTarget.text,
      onPlaybackStart: playback => {
        if (cancelled) return;
        setNarrationPlayback(current => {
          if (current.audioId !== currentNarrationTarget.audioId) return current;

          return {
            ...current,
            isPlaying: true,
            durationMs: playback?.durationMs || current.durationMs,
            wordTimings: playback?.wordTimings || current.wordTimings,
          };
        });
        dispatch(setNarrationPlaying(currentNarrationTarget.audioId));
        startSpeaking();
        queueTutorAudioPrefetch({
          lessonScope: lessonAudioScope,
          entries: upcomingNarrationTargets,
        });
      },
      onPlaybackProgress: playback => {
        if (cancelled) return;
        setNarrationPlayback(current => {
          if (current.audioId !== currentNarrationTarget.audioId) return current;

          return {
            ...current,
            currentTimeMs: playback?.currentTimeMs || 0,
            durationMs: playback?.durationMs || current.durationMs,
          };
        });
      },
      onPlaybackStop: () => {
        stopSpeaking();
        setNarrationPlayback(current => {
          if (current.audioId !== currentNarrationTarget.audioId) return current;

          return {
            ...current,
            isPlaying: false,
          };
        });
      },
    })
      .then(() => {
        if (cancelled) return;
        setNarrationPlayback(current => {
          if (current.audioId !== currentNarrationTarget.audioId) return current;

          const resolvedDurationMs = current.durationMs || fallbackDurationMs;

          return {
            ...current,
            isPlaying: false,
            currentTimeMs: resolvedDurationMs,
            durationMs: resolvedDurationMs,
            complete: true,
          };
        });
        dispatch(setNarrationComplete(currentNarrationTarget.audioId));
      })
      .catch(error => {
        if (cancelled) return;
        stopSpeaking();
        setNarrationPlayback(current => {
          if (current.audioId !== currentNarrationTarget.audioId) return current;

          const resolvedDurationMs = current.durationMs || fallbackDurationMs;

          return {
            ...current,
            isPlaying: false,
            currentTimeMs: resolvedDurationMs,
            durationMs: resolvedDurationMs,
            complete: true,
          };
        });
        dispatch(
          setNarrationError(
            currentNarrationTarget.audioId,
            error?.message || 'Failed to generate tutor audio',
          ),
        );
      });

    return () => {
      cancelled = true;
      stopTutorSpeech();
    };
  }, [
    dispatch,
    currentNarrationTarget?.audioId,
    currentNarrationTarget?.text,
    lessonAudioScope,
    tutorReplayKey,
    upcomingNarrationSignature,
    lcvUnityTutorAudioGateOpen,
  ]);

  useEffect(() => {
    if (phase === 'frontloading') {
      setTrackerMode('full');
      return undefined;
    }

    if (phase === 'knowledge_check_summary') {
      setTrackerMode('full');
      const collapseTimer = window.setTimeout(() => {
        setTrackerMode('mini');
      }, 5000);
      return () => window.clearTimeout(collapseTimer);
    }

    setTrackerMode('mini');
    return undefined;
  }, [phase]);

  const mappedTermStatus = useMemo(() => mapPhaseToTermStatus(phase), [phase]);
  const activeTermName = activeTerm?.term || null;
  const heartbeatContextRef = useRef({
    lessonId,
    termName: null,
    termStatus: null,
  });
  const lastHeartbeatAtRef = useRef(Date.now());
  const currentTermStatusKeyRef = useRef(null);
  const initialHeartbeatSentForRef = useRef(null);
  const resumeAttemptedForRef = useRef(null);
  const prevPhaseRef = useRef(null);
  const masteryReportedRef = useRef(false);
  /** Ref mirror of sendHeartbeat so event listeners (visibility, pagehide) use the latest. */
  const sendHeartbeatRef = useRef(() => { });

  useEffect(() => {
    heartbeatContextRef.current = {
      lessonId,
      termName: activeTermName,
      termStatus: mappedTermStatus,
    };
  }, [lessonId, activeTermName, mappedTermStatus]);

  /** Subscribe to v2 auth-expired events (fires when any v2 call 401s). */
  useEffect(() => {
    const unsubscribe = onV2AuthExpired(() => {
      authExpiredRef.current = true;
      setAuthExpired(true);
    });
    return unsubscribe;
  }, []);

  const sendHeartbeat = useCallback(
    (override = {}) => {
      if (!lessonFetchToken || isMockMode()) return;
      if (authExpiredRef.current) return;
      const ctx = heartbeatContextRef.current;
      const targetLessonId = override.lessonId || ctx.lessonId;
      if (!targetLessonId) return;

      const now = Date.now();
      const elapsedSeconds = Math.max(
        0,
        Math.round((now - lastHeartbeatAtRef.current) / 1000),
      );
      lastHeartbeatAtRef.current = now;

      const durationSeconds =
        override.durationSeconds !== undefined
          ? override.durationSeconds
          : elapsedSeconds;

      postLessonHeartbeat(
        {
          lessonId: targetLessonId,
          term: ctx.termName || undefined,
          termStatus: ctx.termStatus || undefined,
          durationSeconds,
        },
        lessonFetchToken,
        { keepalive: Boolean(override.keepalive) },
      );
    },
    [lessonFetchToken],
  );

  useEffect(() => {
    sendHeartbeatRef.current = sendHeartbeat;
  }, [sendHeartbeat]);

  useEffect(() => {
    masteryReportedRef.current = false;
    initialHeartbeatSentForRef.current = null;
    resumeAttemptedForRef.current = null;
    prevPhaseRef.current = null;
  }, [lessonId]);

  /**
   * Resume from backend: once the viewer model is loaded for this lessonId,
   * fetch /lesson-progress/:lessonId and seek into the saved term + phase.
   * Skipped entirely in mock mode and when no auth token is available.
   */
  useEffect(() => {
    if (!viewerModel || !lessonFetchToken || isMockMode()) return undefined;
    if (authExpired) return undefined;
    if (resumeAttemptedForRef.current === lessonId) return undefined;
    resumeAttemptedForRef.current = lessonId;

    let cancelled = false;
    (async () => {
      const progress = await getLessonProgress(lessonId, lessonFetchToken);
      if (cancelled) return;
      const currentState = progress?.currentState;
      if (currentState?.lastTerm && currentState?.status) {
        dispatch(resumeViewerFromBackend(currentState));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [viewerModel, lessonId, lessonFetchToken, dispatch, authExpired]);

  /** Initial heartbeat when the viewer model first becomes available. */
  useEffect(() => {
    if (!viewerModel || !lessonFetchToken || isMockMode()) return;
    if (authExpired) return;
    if (initialHeartbeatSentForRef.current === lessonId) return;
    initialHeartbeatSentForRef.current = lessonId;
    lastHeartbeatAtRef.current = Date.now();
    currentTermStatusKeyRef.current = `${activeTermName || ''}:${mappedTermStatus || ''}`;
    sendHeartbeat({ durationSeconds: 0 });
  }, [
    viewerModel,
    lessonId,
    lessonFetchToken,
    sendHeartbeat,
    activeTermName,
    mappedTermStatus,
    authExpired,
  ]);

  /** Heartbeat whenever the (term, termStatus) pair changes. */
  useEffect(() => {
    if (!viewerModel || !lessonFetchToken || isMockMode()) return;
    if (authExpired) return;
    if (initialHeartbeatSentForRef.current !== lessonId) return;
    const key = `${activeTermName || ''}:${mappedTermStatus || ''}`;
    if (currentTermStatusKeyRef.current === key) return;
    currentTermStatusKeyRef.current = key;
    sendHeartbeat();
  }, [
    viewerModel,
    lessonId,
    activeTermName,
    mappedTermStatus,
    lessonFetchToken,
    sendHeartbeat,
    authExpired,
  ]);

  /**
   * 30-second heartbeat interval. Skips ticks while the tab is hidden so
   * background time is not counted toward `durationSeconds`. A final
   * heartbeat is flushed on lesson change / unmount.
   */
  useEffect(() => {
    if (!viewerModel || !lessonFetchToken || isMockMode()) return undefined;
    if (authExpired) return undefined;

    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      sendHeartbeat();
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      sendHeartbeat();
    };
  }, [viewerModel, lessonId, lessonFetchToken, sendHeartbeat, authExpired]);

  /**
   * Tab visibility handling: flush a heartbeat when the tab goes hidden so
   * the time the learner was actively viewing is captured, then reset the
   * heartbeat clock on return so time spent hidden does not count toward
   * the next duration.
   */
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (!viewerModel || !lessonFetchToken || isMockMode()) return undefined;
    if (authExpired) return undefined;

    const onVisibilityChange = () => {
      if (document.hidden) {
        sendHeartbeatRef.current?.();
      } else {
        lastHeartbeatAtRef.current = Date.now();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [viewerModel, lessonFetchToken, authExpired]);

  /**
   * pagehide flush with `keepalive: true` so the last seconds are delivered
   * even as the browser tears the page down. pagehide fires on tab close,
   * navigation, and mobile-Safari background, where `beforeunload` does not.
   */
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!viewerModel || !lessonFetchToken || isMockMode()) return undefined;
    if (authExpired) return undefined;

    const onPageHide = () => {
      sendHeartbeatRef.current?.({ keepalive: true });
    };

    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [viewerModel, lessonFetchToken, authExpired]);

  /**
   * Persist per-key-term progress on every meaningful sub-phase
   * transition so the progress tracker can be pre-populated from the
   * backend on a later session (and so the click-to-navigate UI knows
   * which steps the learner has already cleared).
   *
   * Status mapping (matches the lesson-activity heartbeat semantics):
   *   - workbook_ready / workbook_exercise / workbook_options → `workbook`        (LEARN done)
   *   - knowledge_check                                       → `knowledge_check` (LEARN + WORK done)
   *   - knowledge_check_summary                               → `completed`       (LEARN + WORK + CHECK done)
   *
   * The PUT is fire-and-forget; we also update the local map
   * optimistically so the tracker reflects the new state without waiting
   * for a refetch. The backend keeps a high-water mark on
   * questionsCorrect, and we never PUT a status lower than what's
   * already persisted (so revisiting an earlier section can't regress
   * the record).
   */
  useEffect(() => {
    const prev = prevPhaseRef.current;
    prevPhaseRef.current = phase;
    if (prev === phase) return;
    if (!lessonFetchToken || isMockMode() || authExpired) return;
    if (!activeTerm?.term) return;

    let nextStatus = null;
    if (
      phase === 'workbook_ready' ||
      phase === 'workbook_exercise' ||
      phase === 'workbook_options'
    ) {
      nextStatus = 'workbook';
    } else if (phase === 'knowledge_check') {
      nextStatus = 'knowledge_check';
    } else if (phase === 'knowledge_check_summary') {
      nextStatus = 'completed';
    }
    if (!nextStatus) return;

    const TERM_STATUS_RANK = {
      not_started: 0,
      learning: 0,
      workbook: 1,
      knowledge_check: 2,
      completed: 3,
    };
    const termKey = String(activeTerm.term).trim().toLowerCase();
    const existingStatus = keyTermProgress[termKey];
    if ((TERM_STATUS_RANK[nextStatus] || 0) <= (TERM_STATUS_RANK[existingStatus] || 0)) {
      return;
    }

    putKeyTermProgress(
      {
        lessonId,
        term: activeTerm.term,
        questionsAttempted: kcAnswers.length,
        questionsCorrect: kcAnswers.filter(a => a.correct).length,
        status: nextStatus,
      },
      lessonFetchToken,
    );

    dispatch(
      setKeyTermProgress({
        ...keyTermProgress,
        [termKey]: nextStatus,
      }),
    );
  }, [
    phase,
    activeTerm,
    kcAnswers,
    lessonId,
    lessonFetchToken,
    authExpired,
    keyTermProgress,
    dispatch,
  ]);

  const handleNavigateTracker = useCallback(
    target => {
      if (!target || typeof target.termIndex !== 'number' || !target.phase) return;
      stopTutorSpeech();
      stopSpeaking();
      setNarrationPaused(false);
      dispatch(navigateToTrackerNode(target));
    },
    [dispatch],
  );

  /**
   * Send PUT /lesson-progress/:lessonId every time the viewer enters the
   * results phase (covers first attempt and every retake). Ref is reset
   * whenever phase leaves results so the next retake re-sends.
   */
  useEffect(() => {
    if (phase !== 'results') {
      masteryReportedRef.current = false;
      return;
    }
    if (!lessonFetchToken || isMockMode() || authExpired) return;
    if (masteryReportedRef.current) return;
    masteryReportedRef.current = true;

    putLessonProgress(
      lessonId,
      {
        questionsAttempted: masteryScore.total,
        questionsCorrect: masteryScore.correct,
      },
      lessonFetchToken,
    );
  }, [phase, masteryScore, lessonId, lessonFetchToken, authExpired]);

  const showShell =
    phase !== 'tutorial' && !loading && !error && viewerModel;
  const useFrontloadingStage =
    showShell && phase === 'frontloading' && progressData && frontloadingData;
  const useStaticStage =
    showShell && phase !== 'frontloading';

  if (unityWorldLoading) {
    unityOverlayLockedRef.current = true;
  }

  const showUnityLoadingOverlay =
    !skipWorld &&
    Platform.OS === 'web' &&
    (unityWorldLoading || unityOverlayLockedRef.current);

  if (authExpired) {
    return (
      <div className="lcv-root lcv-auth-gate">
        <div className="lcv-auth-modal">
          <div className="lcv-auth-icon">🔒</div>
          <h2 className="lcv-auth-title">Session Expired</h2>
          <p className="lcv-auth-message">
            Your session has ended. Please sign back in to continue your lesson.
          </p>
          <a
            href={WEB_APP_BASE_URL}
            className="lcv-btn-primary lcv-auth-link"
            rel="noopener noreferrer">
            Sign back in
          </a>
        </div>
      </div>
    );
  }

  if (!lessonFetchToken) {
    return (
      <div className="lcv-root lcv-auth-gate">
        <div className="lcv-auth-modal">
          <div className="lcv-auth-icon">🔒</div>
          <h2 className="lcv-auth-title">Access Required</h2>
          <p className="lcv-auth-message">
            This lesson is not available at this address.
            Please use the link provided to you to access your lesson.
          </p>
          <a
            href={WEB_APP_BASE_URL}
            className="lcv-btn-primary lcv-auth-link"
            rel="noopener noreferrer">
            Go to QuikLearn
          </a>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="lcv-root">
        <div className="lcv-loading">
          <div className="lcv-spinner" />
          <p>Loading lesson...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lcv-root">
        <div className="lcv-error">
          <span className="lcv-error-icon">!</span>
          <h2>Unable to load lesson</h2>
          <p>{error}</p>
          <button
            className="lcv-btn-primary"
            onClick={() =>
              dispatch(fetchLessonContentAction(lessonId, lessonFetchToken, { skipWorld }))
            }>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!viewerModel) return null;

  return (
    <div
      className={`lcv-root${tutorChatOpen ? ' lcv-tutor-active' : ''}${
        roomExploreActive ? ' lcv-room-explore-active' : ''
      }`}>
      {!skipWorld && <LessonViewerUnityBackground />}

      {showUnityLoadingOverlay && (
        <div
          className="lcv-unity-world-loading"
          aria-busy={unityWorldLoading}
          aria-live="polite">
          <Loading
            loading={unityWorldLoading}
            color={LCV_UNITY_LOADING_BG}
            label="Loading world…"
            fakeRatePerSecond={LCV_UNITY_LOADING_FAKE_RATE_PER_SEC}
            onExitAnimationComplete={onUnityLoadingExitAnimationComplete}
          />
        </div>
      )}

      {showShell && !roomExploreActive && (
        <LCVActionSidebar onExit={handleExit} />
      )}

      {phase === 'tutorial' && !roomExploreActive && (
        <TutorialPhase onComplete={() => dispatch(setTutorialViewed())} />
      )}

      {useFrontloadingStage && !roomExploreActive && (
        <LCVFrontloadingStage
          data={frontloadingData}
          tutorText={tutorText}
          metadata={metadata}
          progressData={progressData}
          phase={phase}
          trackerMode={trackerMode}
          onNavigateTracker={handleNavigateTracker}
          visualContent={visualContent}
          startDisabled={!isNarrationGateOpen}
          tutorAnimationProgress={tutorAnimationProgress}
          tutorWordTimings={narrationPlayback.wordTimings}
          tutorCurrentTimeMs={narrationPlayback.currentTimeMs}
          tutorFontSize={tutorFontSize}
          onTutorFontSizeChange={setTutorFontSize}
          onPauseTutor={handlePauseTutor}
          onResumeTutor={handleResumeTutor}
          onSkipTutor={handleSkipTutor}
          playbackRate={tutorPlaybackRate}
          canIncreasePlaybackRate={canIncreaseTutorPlaybackRate}
          canDecreasePlaybackRate={canDecreaseTutorPlaybackRate}
          onIncreasePlaybackRate={handleIncreaseTutorPlaybackRate}
          onDecreasePlaybackRate={handleDecreaseTutorPlaybackRate}
          volume={tutorVolume}
          canIncreaseVolume={canIncreaseTutorVolume}
          canDecreaseVolume={canDecreaseTutorVolume}
          onIncreaseVolume={handleIncreaseTutorVolume}
          onDecreaseVolume={handleDecreaseTutorVolume}
          isMuted={isTutorMuted}
          onToggleMute={handleToggleTutorMute}
          canPauseTutor={canPauseTutor}
          canSkipTutor={canSkipTutor}
          onStart={() => {
            if (isNarrationGateOpen) {
              stopTutorSpeech();
              stopSpeaking();
              setNarrationPaused(false);
              dispatch(advancePhase());
            }
          }}
          paused={tutorChatOpen || narrationPaused}
          tutorPlaybackFinished={narrationPlayback.complete}
        />
      )}

      {useStaticStage && !roomExploreActive && (
        <LCVStaticLessonStage
          metadata={metadata}
          progressData={progressData}
          phase={phase}
          activeTerm={activeTerm}
          onNavigateTracker={handleNavigateTracker}
          tutorText={tutorText}
          tutorAnimationProgress={tutorAnimationProgress}
          tutorWordTimings={narrationPlayback.wordTimings}
          tutorCurrentTimeMs={narrationPlayback.currentTimeMs}
          tutorPlaybackFinished={narrationPlayback.complete}
          tutorPaused={narrationPaused}
          canPauseTutor={canPauseTutor}
          tutorFontSize={tutorFontSize}
          onTutorFontSizeChange={setTutorFontSize}
          onPauseTutor={handlePauseTutor}
          onResumeTutor={handleResumeTutor}
          onSkipTutor={handleSkipTutor}
          onReplayTutor={handleReplayTutor}
          playbackRate={tutorPlaybackRate}
          canIncreasePlaybackRate={canIncreaseTutorPlaybackRate}
          canDecreasePlaybackRate={canDecreaseTutorPlaybackRate}
          onIncreasePlaybackRate={handleIncreaseTutorPlaybackRate}
          onDecreasePlaybackRate={handleDecreaseTutorPlaybackRate}
          volume={tutorVolume}
          canIncreaseVolume={canIncreaseTutorVolume}
          canDecreaseVolume={canDecreaseTutorVolume}
          onIncreaseVolume={handleIncreaseTutorVolume}
          onDecreaseVolume={handleDecreaseTutorVolume}
          isMuted={isTutorMuted}
          onToggleMute={handleToggleTutorMute}
          trackerMode={trackerMode}
          canReplayTutor={Boolean(currentNarrationTarget?.audioId) && narrationPlayback.complete}
          canSkipTutor={canSkipTutor}
          visualContent={visualContent}
          middleContent={
            <>
              {phase === 'learning' && activeTerm && (
                <LearningPhase
                  term={activeTerm}
                  termIndex={activeTermIndex}
                  continueLabel={
                    (activeTerm?.learningSteps?.length || 0) > activeLearningStepIndex + 1
                      ? 'Continue →'
                      : 'Ready for the Workbook →'
                  }
                  continueDisabled={!isNarrationGateOpen}
                  onContinue={() => dispatch(advancePhase())}
                />
              )}

              {(phase === 'workbook_ready' || phase === 'workbook_exercise') &&
                activeTerm && (
                  <WorkbookReadyPhase
                    workbook={activeWorkbook}
                    workbookState={workbookState}
                    navigationDisabled={!isNarrationGateOpen}
                    onReady={() => dispatch(setWorkbookReady())}
                    onDone={() => dispatch(setWorkbookDone())}
                  />
                )}

              {phase === 'workbook_options' && activeTerm && (
                <WorkbookOptionsPhase
                  workbook={activeWorkbook}
                  workbookState={workbookState}
                  continueDisabled={!isNarrationGateOpen}
                  onSubmit={idx => dispatch(submitWorkbookAnswer(idx))}
                  onContinue={() => dispatch(advancePhase())}
                />
              )}

              {phase === 'knowledge_check' && activeKCQuestion && (
                <KnowledgeCheckPhase
                  question={activeKCQuestion}
                  questionIndex={activeQuestionIndex}
                  totalQuestions={activeTerm?.knowledgeCheck?.questions?.length || 0}
                  kcAnswers={kcAnswers}
                  nextDisabled={!isNarrationGateOpen}
                  onSubmit={answer => dispatch(submitKCAnswer(answer))}
                  onNext={() => dispatch(advanceKCQuestion())}
                />
              )}

              {phase === 'knowledge_check_summary' && (
                <KCSummaryPhase
                  score={kcScore}
                  termName={activeTerm?.term}
                  continueDisabled={!isNarrationGateOpen}
                  onContinue={() => dispatch(kcSummaryDone())}
                />
              )}

              {phase === 'mastery' && activeMasteryQuestion && (
                <MasteryPhase
                  question={activeMasteryQuestion}
                  questionIndex={activeQuestionIndex}
                  totalQuestions={masteryQuestions.length}
                  masteryAnswers={masteryAnswers}
                  nextDisabled={!isNarrationGateOpen}
                  onSubmit={answer => dispatch(submitMasteryAnswer(answer))}
                  onNext={() => dispatch(advanceMasteryQuestion())}
                />
              )}

              {phase === 'results' && (
                <ResultsPhase
                  score={masteryScore}
                  terms={viewerModel.terms}
                  masteryAnswers={masteryAnswers}
                  nextLessonLoading={nextLessonLoading}
                  nextLessonUnavailable={nextLessonUnavailable}
                  nextLessonError={nextLessonError}
                  onReviewTerm={handleReviewTerm}
                  onGoToNextLesson={handleGoToNextLesson}
                  onDoneNavigate={handleExit}
                  onRetakeMastery={handleRetakeMastery}
                  showRoomExplore={showRoomExploreOffer}
                  onRoomExplore={startRoomExplore}
                />
              )}
            </>
          }
        />
      )}

      {showShell && !roomExploreActive && (
        <LCVTutorChatPanel
          open={tutorChatOpen}
          onClose={handleCloseTutorChat}
          token={authToken}
          greeting={tutorChatGreeting}
          currentContext={tutorChatContext}
        />
      )}

      {showDevTools && viewerModel && !roomExploreActive && (
        <LCVDevStrip
          metadata={metadata}
          phase={phase}
          activeTermIndex={activeTermIndex}
          activeQuestionIndex={activeQuestionIndex}
          terms={viewerModel.terms}
          masteryQuestionCount={masteryQuestions.length}
          onJump={node => dispatch(jumpToDevTimelineNode(node))}
        />
      )}

      {roomExploreActive && (
        <div className="lcv-room-explore-dock" role="region" aria-label="Room explore">
          <button
            type="button"
            className="lcv-btn-primary"
            onClick={endRoomExploreSession}>
            Back to lesson
          </button>
          <div className="lcv-room-explore-hint">
            You will return to the lesson view automatically after five minutes.
          </div>
        </div>
      )}
    </div>
  );
};

export default LessonContentViewer;
